import {
  AttachmentPreview,
  BoxV2 as Box,
  Button,
  MultimediaList,
  TextInput,
  AttachmentInfo,
} from 'portal-commons';
import { FunctionComponent, useEffect, useState } from 'react';
import { useForm, useController } from 'react-hook-form';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
} from '@material-ui/core';

import AppealCategory from '../AppealCategory';
import SampleMultimediaUploader from '../SampleMultimediaUploader';
import {
  deleteAttachment,
  downloadAttachmentToDesktop,
  uploadAttachment,
} from '../../apiServices';
import { AppealCategoryData, AppealRequestData } from '../../types';
import { isValidInput, toastFlashMessage } from '@/utils';

interface AuthplusAppealModalProps {
  open?: boolean;
  submitting?: boolean;
  categories?: AppealCategoryData[];
  disabledCategoryIds?: string[];
  onClose?: () => void;
  onSubmit?: (data: AppealRequestData) => void;
}

interface FormValues {
  explanation: string;
  categories: string[];
}

const useStyles = makeStyles(() => ({
  container: {
    width: '700px',
    maxWidth: 'unset',
    padding: '35px 30px 25px 30px',
    marginLeft: '200px',
    boxSizing: 'border-box',
    lineHeight: 'normal',
  },
  title: {
    padding: '0',
    '& > *': {
      textAlign: 'center',
      fontWeight: 500,
      fontSize: '20px',
      lineHeight: '23px',
      color: '#071822',
    },
  },
  content: {
    color: '#333E41',
  },
}));

const ATTACHMENT_LIMIT = 10;

const AuthplusAppealModal: FunctionComponent<AuthplusAppealModalProps> = ({
  open = false,
  submitting = false,
  categories = [],
  disabledCategoryIds = [],
  onClose,
  onSubmit,
}) => {
  const classes = useStyles();
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<FormValues>({
    defaultValues: {
      explanation: '',
      categories: [],
    },
  });
  const {
    field: { value: selectedCategories, onChange: setSelectedCategories },
    fieldState: { error: selectedCategoriesError },
  } = useController({
    control,
    name: 'categories',
    rules: {
      validate: {
        required: (value) =>
          value.length > 0 || 'At least one category is required',
      },
    },
  });
  const {
    field: { value: explanation, onChange: setExplanation },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        maxLength: (value) =>
          value.length <= 1024 || 'Maximum 1024 chars are allowed',
        patter: (val) => isValidInput(val) || 'Invalid input',
      },
    },
  });
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentInfo[]>([]);
  const [attachmentPreviewIndex, setAttachmentPreviewIndex] = useState(-1);

  useEffect(() => {
    if (!open) {
      reset();
      setLoading(false);
      setAttachments([]);
      setAttachmentPreviewIndex(-1);
    }
  }, [open]);

  const handleValid = (values: FormValues) => {
    onSubmit?.({
      ...values,
      attachments: attachments.map((attachment) => attachment.uuid),
    });
  };

  const handleDownload = async (index: number) => {
    try {
      await downloadAttachmentToDesktop(
        attachments[index].uuid,
        attachments[index].fileName
      );
    } catch (e) {
      // ignore
    }
  };

  const handleUpload = async (file: File) => {
    setLoading(true);
    const originalAttachments = attachments.slice();
    try {
      const attachment: AttachmentInfo = await uploadAttachment(file);
      setAttachments([
        ...attachments,
        {
          ...attachment,
          file,
        },
      ]);
    } catch (e) {
      setAttachments(originalAttachments);
    }
    setLoading(false);
  };

  const handlePreview = async (index: number) => {
    setAttachmentPreviewIndex(index);
  };

  const handleDelete = async (index: number) => {
    const removedAttachment = attachments[index];
    const newAttachments = attachments.filter((_, idx) => idx !== index);
    const originalAttachments = attachments.slice();
    setAttachments(newAttachments);
    try {
      await deleteAttachment(removedAttachment.uuid);
    } catch (e) {
      setAttachments(originalAttachments);
    }
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog
      data-testid="appealRequestModal"
      classes={{
        paper: classes.container,
      }}
      open={open}
    >
      <DialogTitle
        data-testid="appealRequestModalTitle"
        classes={{
          root: classes.title,
        }}
      >
        Authentication+ Appeal
      </DialogTitle>
      <form noValidate onSubmit={handleSubmit(handleValid)}>
        <DialogContent
          data-testid="appealRequestModalContent"
          classes={{ root: classes.content }}
        >
          <Box as="p" sx={{ mt: 'l', mb: 'm' }}>
            You can submit an Appeal up to 45 days after the completion of a
            vet. Appeals are charged $11.00 regardless of the outcome.
          </Box>
          <Box as="h3" sx={{ fontWeight: 'bold', fontSize: 'H500', mb: 'xxs' }}>
            Appeal Categories
          </Box>
          <Box
            as="p"
            sx={{
              mb: 'm',
              color: selectedCategoriesError?.message
                ? 't.paleRed600'
                : undefined,
            }}
          >
            You are required to select at least one category, but may select
            more
          </Box>
          {categories.map((category) => (
            <Box key={category.appealCategoryId} sx={{ mb: 's' }}>
              <AppealCategory
                disabled={disabledCategoryIds.includes(
                  category.appealCategoryId
                )}
                checked={selectedCategories.includes(category.appealCategoryId)}
                title={category.displayName}
                message={category.description}
                onValueChange={(checked) => {
                  const categoryId = category.appealCategoryId;
                  const newValue = checked
                    ? [...selectedCategories, categoryId]
                    : selectedCategories.filter((id) => id !== categoryId);
                  setSelectedCategories(newValue);
                }}
                contentColor="#333E41"
              />
            </Box>
          ))}

          <Box as="small" sx={{ color: 't.grey500', fontSize: 'H100' }}>
            * Indicates a Required Field
          </Box>
          <Box sx={{ mt: 's', mb: 's' }}>
            <TextInput
              data-testid="appealRequestModalExplanationInput"
              label="Explanation"
              value={explanation}
              onChange={setExplanation}
              error={!!explanationError?.message}
              note={explanationError?.message}
              maxLength={1024}
            />
          </Box>
          <SampleMultimediaUploader
            loading={loading}
            onChange={handleUpload}
            disabled={attachments.length >= ATTACHMENT_LIMIT}
            max={ATTACHMENT_LIMIT}
            data-testid="appealRequestModalFileUploader"
          />
          <Box sx={{ mt: 's', mb: 'm' }}>
            <MultimediaList
              title="Sample Multimedia Files"
              emptyMessage="No files have been attached"
              editable
              attachments={attachments}
              onDownload={handleDownload}
              onSelect={handlePreview}
              onDelete={handleDelete}
              loading={
                attachmentPreviewIndex > -1 &&
                !attachments[attachmentPreviewIndex].file
              }
              data-testid="appealRequestSampleMultiMediaList"
              style={{ minHeight: 160 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'center',
              gap: 'l',
            }}
          >
            <Button
              variant="outline"
              size="medium"
              disabled={loading || submitting}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="standard"
              size="medium"
              disabled={
                loading ||
                submitting ||
                Object.values(errors).some((error) => !!error?.message)
              }
            >
              Submit
            </Button>
          </Box>
        </DialogActions>
      </form>

      {attachmentPreviewIndex > -1 &&
        attachments[attachmentPreviewIndex].file && (
          <AttachmentPreview
            data-testid="appealRequestModalBackdrop"
            file={attachments[attachmentPreviewIndex].file!}
            mimeType={attachments[attachmentPreviewIndex].mimeType}
            onClose={() => setAttachmentPreviewIndex(-1)}
          />
        )}
    </Dialog>
  );
};

export default AuthplusAppealModal;
